import { MessageSquareWarning } from "lucide-react";
import { conflictExtraCases } from "./conflictCases";

// Conflict Resolution & Difficult Conversations
// Maps to Cert IV communication / teamwork / client-service competencies.
// 5 beats: Assess → Open & acknowledge → De-escalation toolkit → Resolve & set
// boundaries → Document & debrief. Graded /100 (20/20/25/20/15).
export const conflictConfig = {
  simId: "conflict",
  simName: "VetSim Communication",
  subtitle: "Conflict resolution trainer",
  title: "Conflict Resolution & Difficult Conversations",
  accent: "#7c3aed",
  Icon: MessageSquareWarning,
  topic: "conflict",
  conversation: true,
  steps: ["Read the room", "Open & acknowledge", "De-escalate", "Resolve", "Document"],
  introBlurb:
    "Handle the conversations that actually rattle a veterinary team — an angry client at the desk, grief showing up as aggression, a threat to post a bad review, a tense colleague. Across five beats you'll read the real driver, open with empathy, de-escalate, resolve while keeping professional and safety boundaries, then document it cleanly. Graded against a Cert IV communication rubric with coaching on every choice.",
  cases: [
    ...conflictExtraCases,
    {
      id: "billing-dispute",
      name: "Mr Patel — the bill is higher than the estimate",
      meta: "Front desk · busy waiting room",
      briefing:
        "Mr Patel is collecting his dog after a dental. The invoice is $480 more than the phone estimate (extra extractions were needed). He's leaning over the desk, voice raised, saying 'This is a rip-off — nobody told me.' Other clients are watching.",
      beats: [
        {
          type: "choice", key: "assess", label: "Read the room",
          title: "What's really driving this?",
          prompt: "Before you respond, read the situation. What is most likely fuelling Mr Patel's anger?",
          options: [
            { t: "Bill shock and feeling he wasn't consulted about the added cost — he feels blindsided and out of control, not simply 'cheap'.", score: 3, note: "Correct — the anger is about surprise and lack of consent/communication, not the dollar figure alone. Address the feeling first." },
            { t: "He's just a difficult sort who's trying it on to wangle a discount out of the extra charge on the bill.", score: 0, note: "Labelling the client as 'difficult' shuts down empathy and escalates conflict." },
            { t: "He clearly doesn't care all that much about his dog, and is really only bothered about the money side of it.", score: 0, note: "An unfair assumption; most cost anger comes from worry plus surprise." },
          ],
          full: 20,
        },
        {
          type: "choice", key: "open", label: "Open & acknowledge",
          title: "Your opening line",
          prompt: "Mr Patel is mid-sentence and heated. What do you say first?",
          options: [
            { t: "\"I can see you're really upset about this, and I don't blame you — getting a bill that's bigger than you expected is a horrible surprise. Let's go through it together.\"", score: 3, note: "Acknowledges the emotion, validates it, and offers partnership — textbook de-escalation opening." },
            { t: "\"Actually, if you check the consent form you signed, it does clearly say that the estimate can change if more work is needed.\"", score: 0, note: "Leading with the contract is defensive and 'wins the argument, loses the client'. Empathy comes before facts." },
            { t: "\"There's honestly nothing I can do about the figure, I'm afraid — that's simply what the treatment ends up costing.\"", score: 0, note: "Dismissive and final — guaranteed to escalate." },
          ],
          full: 20,
        },
        {
          type: "multi", key: "deesc", label: "De-escalation toolkit",
          title: "De-escalate",
          prompt: "Tap every technique you'd genuinely use right now. Avoid the traps.",
          chips: [
            { t: "Lower your own voice and slow down", flag: true, coach: "Calm, slow speech is contagious and lowers the temperature." },
            { t: "Move him away from the watching waiting room to a quiet consult room", flag: true, coach: "Privacy removes the audience and the need to 'perform' anger." },
            { t: "Name the emotion: 'I can hear how frustrated you are'", flag: true, coach: "Naming the feeling makes the client feel heard and reduces the charge." },
            { t: "Let him finish without interrupting", flag: true, coach: "Interrupting a venting client restarts the anger; let it run down." },
            { t: "Tell him to calm down or you won't help him", flag: false, coach: "" },
            { t: "Argue the clinical justification line-by-line immediately", flag: false, coach: "" },
            { t: "Match his volume so he knows you're serious", flag: false, coach: "" },
          ],
          max: 25,
        },
        {
          type: "choice", key: "resolve", label: "Resolve & set boundaries",
          title: "Resolve it",
          prompt: "He's calmer. How do you move toward a resolution?",
          options: [
            { t: "Explain clearly why the extra extractions were needed, apologise that he wasn't called for verbal consent at the time, and offer to get the vet/practice manager to discuss the bill and a payment plan.", score: 3, note: "Owns the genuine process failure (no intra-op consent call), explains, and offers a constructive path within your authority." },
            { t: "Quietly waive the whole extra $480 yourself to make him go away.", score: 1, note: "Discounting beyond your authority isn't your call and sets a precedent — escalate the financial decision to the vet/manager instead." },
            { t: "Insist the full amount is due now and refuse to release the dog otherwise.", score: 0, note: "Hard-lining payment in a heated moment destroys trust; offer options and involve a manager." },
          ],
          full: 20,
        },
        {
          type: "choice", key: "doc", label: "Document & debrief", last: true,
          title: "Document & debrief",
          prompt: "The conversation is resolved. How do you close it out?",
          options: [
            { t: "Record the interaction factually in the client file — what was discussed, the consent gap, the payment-plan agreed and who is following up — flag the intra-op consent process to the team, and check in with yourself/colleagues if you're shaken.", score: 3, note: "Objective, factual notes; a system fix flagged; and self-care/peer support after a hard interaction." },
            { t: "Write 'client was aggressive and rude about his bill' in the file and leave it at that, then move on to the next job.", score: 0, note: "Emotive, judgemental notes are unprofessional and unhelpful — document behaviour and facts objectively." },
            { t: "Don't bother writing anything down at all, since it's all been resolved now and there's really no need to record it.", score: 0, note: "Difficult interactions and any agreements must be documented for the team and continuity." },
          ],
          full: 15,
        },
      ],
    },
    {
      id: "grief-aggression",
      name: "Ms Hayes — anger after a grave prognosis",
      meta: "Consult room · just given bad news",
      briefing:
        "The vet has just told Ms Hayes her young cat has aggressive lymphoma. She's now shouting at you in reception: 'You people missed this! You're useless!' She's tearful and trembling.",
      beats: [
        {
          type: "choice", key: "assess", label: "Read the room",
          title: "What's really happening?",
          prompt: "How do you read Ms Hayes' outburst?",
          options: [
            { t: "This is acute grief and shock presenting as anger — the aggression is about devastation and helplessness, not a genuine complaint about your competence.", score: 3, note: "Correct — grief commonly surfaces as anger. Meet the emotion, don't defend." },
            { t: "She's effectively accusing the clinic of negligence, so the right thing to do is stand up for the clinic's record.", score: 0, note: "Defending facts now ignores her grief and pours fuel on it." },
            { t: "She's being abusive towards staff in reception, so she really ought to be asked to leave the premises immediately.", score: 0, note: "Premature; this is grief. Boundaries matter, but lead with compassion first." },
          ],
          full: 20,
        },
        {
          type: "choice", key: "open", label: "Open & acknowledge",
          title: "Your opening line",
          prompt: "What do you say?",
          options: [
            { t: "\"I am so sorry. This is devastating news and it's completely unfair. Come and sit down with me for a moment.\"", score: 3, note: "Compassion, validation, and a gentle move to a calmer space — exactly right for grief." },
            { t: "\"We did absolutely everything correctly here — if you take a look at the records, you'll see that for yourself.\"", score: 0, note: "Self-justification in the face of grief is heard as cold and dismissive." },
            { t: "\"Please don't shout at me like that — none of this is actually my fault, and I'm only trying my best to help you.\"", score: 0, note: "Makes it about you; abandons the grieving client." },
          ],
          full: 20,
        },
        {
          type: "multi", key: "deesc", label: "Support toolkit",
          title: "Support & de-escalate",
          prompt: "Tap what you'd genuinely do for a grieving, angry owner.",
          chips: [
            { t: "Offer tissues, water and a private space", flag: true, coach: "Small acts of care signal safety and dignity." },
            { t: "Sit at her level and give her time and silence", flag: true, coach: "Presence beats words in acute grief." },
            { t: "Acknowledge the loss and avoid rushing to logistics", flag: true, coach: "Don't jump to forms and payment while she's in shock." },
            { t: "Offer to bring the vet back to answer questions when she's ready", flag: true, coach: "Keeps clinical questions with the right person, at her pace." },
            { t: "Tell her crying won't help and she needs to decide now", flag: false, coach: "" },
            { t: "Immediately push euthanasia paperwork and pricing", flag: false, coach: "" },
            { t: "Walk away and leave her alone in reception", flag: false, coach: "" },
          ],
          max: 25,
        },
        {
          type: "choice", key: "resolve", label: "Resolve & boundaries",
          title: "Where to next",
          prompt: "She's calmer but exhausted. What's the constructive next step?",
          options: [
            { t: "Gently offer options: a quiet room to gather herself, a call-back to discuss treatment vs palliative care with the vet, and written info and grief-support resources to take home.", score: 3, note: "Gives control back, paces decisions, and points to support — compassionate and practical." },
            { t: "Pressure her to make a treatment decision immediately so the room is free.", score: 0, note: "Rushing a grieving owner is unkind and leads to regretted decisions." },
            { t: "Tell her there's nothing more anyone can do and send her home.", score: 0, note: "Closes the door on legitimate options (treatment/palliative care) and support." },
          ],
          full: 20,
        },
        {
          type: "choice", key: "doc", label: "Document & debrief", last: true,
          title: "Document & debrief",
          prompt: "How do you close out?",
          options: [
            { t: "Note the conversation and follow-up plan factually, alert the team she's grieving so everyone is gentle, ensure the vet call-back is booked, and take a moment yourself — these interactions are emotionally heavy.", score: 3, note: "Factual record, team awareness, follow-up secured, and acknowledges your own wellbeing." },
            { t: "Record it simply as 'client became hostile and accusatory in reception', without any of the surrounding context.", score: 0, note: "Judgemental wording; document behaviour and context neutrally, recognising grief." },
            { t: "Skip documenting it altogether, since no treatment was actually decided on and the consult is effectively over now.", score: 0, note: "The interaction, the emotional context and the call-back plan all need recording for continuity." },
          ],
          full: 15,
        },
      ],
    },
    {
      id: "review-threat",
      name: "Ms Lowe — 'I'll post a 1-star review'",
      meta: "Phone · post-visit complaint",
      briefing:
        "Ms Lowe phones, furious about a long wait at yesterday's visit. She says: 'If you don't refund my consult I'll put you all over Google and report you to the board.' She is not abusive, just threatening reputational action.",
      beats: [
        {
          type: "choice", key: "assess", label: "Read the room",
          title: "What's the real issue?",
          prompt: "How do you interpret the review threat?",
          options: [
            { t: "She feels unheard about a genuine service problem (the wait) and is using leverage to be taken seriously — the underlying complaint is valid and worth addressing.", score: 3, note: "Correct — treat the legitimate grievance, not the threat. Most 'review threats' are a bid to be heard." },
            { t: "She's effectively blackmailing the clinic over the bill, so on principle you should simply refuse to give in to it.", score: 0, note: "Framing it as blackmail makes you defensive and ignores a real service issue." },
            { t: "Online reviews don't really matter all that much, so you can comfortably just ignore her and let it blow over.", score: 0, note: "Dismissing both the client and the feedback guarantees the bad review and loses the chance to fix it." },
          ],
          full: 20,
        },
        {
          type: "choice", key: "open", label: "Open & acknowledge",
          title: "Your opening line",
          prompt: "What do you say?",
          options: [
            { t: "\"I'm really sorry you were kept waiting so long yesterday — that's frustrating and your time matters. I'd genuinely like to make this right. Can you tell me what happened?\"", score: 3, note: "Validates the grievance, signals you want to fix it, and invites her story — defuses the threat by addressing the cause." },
            { t: "\"We don't respond to threats. If you post a review, that's your choice.\"", score: 0, note: "Adversarial; turns a recoverable client into a guaranteed bad review." },
            { t: "\"Fine, I'll refund you, just please don't post anything.\"", score: 1, note: "Caving to the threat without understanding the issue rewards leverage and may exceed your authority — address the problem first." },
          ],
          full: 20,
        },
        {
          type: "multi", key: "deesc", label: "Handling toolkit",
          title: "De-escalate the call",
          prompt: "Tap the techniques you'd use on this call.",
          chips: [
            { t: "Listen fully and let her explain before responding", flag: true, coach: "Being heard is most of what she wants." },
            { t: "Apologise specifically for the wait and its impact", flag: true, coach: "A specific, sincere apology lands far better than a generic 'sorry'." },
            { t: "Stay calm and don't take the threat personally", flag: true, coach: "Regulate yourself; the threat is leverage, not a personal attack." },
            { t: "Explain what the clinic will do to prevent it next time", flag: true, coach: "A concrete fix shows the feedback was valued." },
            { t: "Threaten legal action over a defamatory review", flag: false, coach: "" },
            { t: "Argue that the wait 'wasn't that long'", flag: false, coach: "" },
            { t: "Hang up because she mentioned the board", flag: false, coach: "" },
          ],
          max: 25,
        },
        {
          type: "choice", key: "resolve", label: "Resolve & boundaries",
          title: "Resolve it",
          prompt: "She's calmer. What's the right resolution?",
          options: [
            { t: "Acknowledge the service failure, explain the cause and the fix, and escalate the refund/goodwill decision to the practice manager — making clear any outcome is because it's the right thing, not because of the threat.", score: 3, note: "Separates the genuine remedy from the threat and routes the financial call to the right authority." },
            { t: "Refuse to offer her any goodwill at all, purely because she went and threatened the clinic with a bad review.", score: 0, note: "Punishing valid feedback over the threat is counter-productive and unfair." },
            { t: "Promise her a full refund plus a couple of free future visits right there on the spot, to make the threat go away.", score: 1, note: "Over-promising beyond your authority creates problems; offer to escalate the decision instead." },
          ],
          full: 20,
        },
        {
          type: "choice", key: "doc", label: "Document & debrief", last: true,
          title: "Document & debrief",
          prompt: "How do you close out?",
          options: [
            { t: "Log the complaint factually, record the agreed follow-up and who owns it, raise the waiting-time issue with the manager as a process improvement, and note that the review threat was made — without emotive language.", score: 3, note: "Objective complaint record, a system improvement raised, and follow-up ownership clear." },
            { t: "Write it up simply as 'client threatened us and demanded money', without noting the underlying service issue at all.", score: 0, note: "Emotive and adversarial; document the facts and the service issue neutrally." },
            { t: "Don't log the complaint at all, in case having it on record only encourages her to take it further as a formal complaint.", score: 0, note: "Never hide complaints — they must be recorded and learned from." },
          ],
          full: 15,
        },
      ],
    },
  ],
};
